<template>
    <div class="head">
        <div class="title">{{ trans.cart.title }}</div>
        <GoBackButton @click.prevent="onGoBackButtonClick" />
        <UserMenuComponent />
    </div>

    <div ref="itemListElement" class="itemlist scrollable">
        <div v-if="isEmpty" class="nodata">
            <div class="lbl">{{ trans.cart.noItems }}</div>
            <a class="backlink" @click="onGoBackButtonClick">{{ trans.cart.backToOverview }}</a>
        </div>
        <CartItem
            v-for="item in cartItems"
            :key="item.id"
            :item="item"
            :disabled="isPerformingAction"
            @decrease="onDecreaseItemSelected"
            @increase="onIncreaseItemSelected"
            @remove="onRemoveItemSelected"
        />
        <!-- todo: in theory we could use this component instead of the above, but it has some issues -->
        <!-- the option group names are shown in original component, but not in new one -->
        <!-- <CartItem2
            v-for="item in cartItems"
            :key="item.id"
            :item="item"
            :disabled="isPerformingAction"
            :showInternalNames="false"
        /> -->
    </div>

    <div class="bottom">
        <div class="selectionsum">{{ selectedArticlesText }}</div>
        <PayButtonComponent
            v-if="isOrderMode"
            :action="trans.general.confirm"
            :disabled="!isPayButtonEnabled"
            :amount="totalAmount"
            @click="onProceedButtonClick"
        />
        <PayButtonComponent v-if="!isOrderMode" :action="trans.pay.orderWithStaff" :disabled="true" />
    </div>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { CartController_New } from "@/app/controller/CartController_new";
    import { trans } from "@/app/localization/Trans";
    import { MinimumAgePopover } from "@/app/popover/MinimumAgePopover";
    import { Helper } from "@/app/ui/Helper";
    import { Popup } from "@/app/ui/Popup";
    import type { ICartItem } from "@/data/model/Customer";
    import { CartControllerMode, ServiceType } from "@/data/model/Enums";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

    import CartItem from "@/app/components/cart/CartItem.vue";
    import PayButtonComponent from "@/app/components/footer/PayButtonComponent.vue";
    import GoBackButton from "@/app/components/header/GoBackButton.vue";
    import UserMenuComponent from "@/app/components/header/UserMenuComponent.vue";

    // import CartItem2 from "@/components/cart/CartItem.vue";

    // header
    const isViewVisible = ref(false);

    CartController_New.onShown.add(onBecomeVisible);
    CartController_New.onHidden.add(onBecomeInvisible);
    CartController_New.onUpdateMode.add(updateMode);

    // init components
    onMounted(() => {
        const service = Helper.getCurrentOrderOrOfferService();
        const cartMode =
            service!.serviceType === ServiceType.Offer ? CartControllerMode.Offer : CartControllerMode.Order;

        constructor(cartMode);
    });

    onBeforeUnmount(() => {
        TastierClient.context.onCartChanged.remove(updateCart);
    });

    const selectedArticles = ref(0);
    const selectedArticlesText = computed(() => trans.cart.itemsSelected.replace("{0}", selectedArticles.value + ""));

    const currentMode = ref<CartControllerMode>(CartControllerMode.Order);
    const isOrderMode = computed(() => currentMode.value === CartControllerMode.Order);

    const totalAmount = ref<string>(toMoneyWithSign(0, true));
    const isPayButtonEnabled = ref(false);

    const itemListElement = ref<HTMLDivElement | null>(null);
    const $itemList = computed<JQuery | null>(() => (itemListElement.value ? $(itemListElement.value) : null));

    const cartItems = ref<Array<ICartItem>>([]);

    function constructor(mode: CartControllerMode) {
        currentMode.value = mode;
        isPayButtonEnabled.value = false;

        onBecomeVisible();
    }

    async function onBecomeVisible() {
        isViewVisible.value = true;

        TastierClient.context.onCartChanged.remove(updateCart);
        TastierClient.context.onCartChanged.add(updateCart);
        TastierClient.context.onCartDiscountChanged.remove(onCartDiscountChanged);
        TastierClient.context.onCartDiscountChanged.add(onCartDiscountChanged);
        $itemList.value?.scrollTop(0);

        updateCart(true);
    }

    function onBecomeInvisible() {
        isViewVisible.value = false;
        TastierClient.context.onCartChanged.remove(updateCart);
        TastierClient.context.onCartDiscountChanged.remove(onCartDiscountChanged);
    }

    function updateMode(mode: CartControllerMode) {
        currentMode.value = mode;
    }

    function onGoBackButtonClick() {
        CartController_New.onGoBack.dispatch();
    }

    function onProceedButtonClick() {
        checkMinimumAgeAndContinue();
    }

    const isEmpty = ref(false);

    async function onCartDiscountChanged() {
        updateCart(true);
    }

    async function updateCart(isUserAction: boolean) {
        const offer = TastierClient.context.currentOffer;
        let articles = 0;

        if (currentMode.value === CartControllerMode.Order && offer !== undefined && offer.cart !== undefined) {
            totalAmount.value = toMoneyWithSign(offer.cart.totalAfterDiscountsAndVouchers);
            isPayButtonEnabled.value = offer.cart.items.length > 0 && offer.cart.totalAfterDiscountsAndVouchers >= 0;
        } else {
            isPayButtonEnabled.value = false;
        }

        if (offer !== undefined && offer.cart !== undefined) {
            for (const item of offer.cart.items) {
                articles += item.quantity;
            }
        }

        selectedArticles.value = articles;

        if (!isUserAction) {
            Popup.showWithOptions({
                message: `<b>${translations.pay.cartChangedTitle}</b><br /><br />${translations.pay.cartChangedMessage}`,
                uniqueId: "totalChanged",
            });
        }

        if ($itemList.value === null) {
            logError("itemListElement is not set");
        }

        // get last scroll position
        const lastScrollPos = $itemList.value?.scrollTop() ?? 0;

        if (offer === undefined || offer.cart === undefined || offer.cart.items.length === 0) {
            cartItems.value = [];
            isEmpty.value = true;
            return;
        }

        isEmpty.value = false;

        cartItems.value = offer!.cart!.items;
        await nextTick();

        // updateCommentEditors(offer.cart);

        // scroll to last position
        $itemList.value?.scrollTop(lastScrollPos);
    }

    const isPerformingAction = ref(false);

    function checkMinimumAgeAndContinue() {
        const offer = TastierClient.context.currentOffer;
        if (offer === undefined || offer.cart === undefined) return;

        let minAge = 0;

        const searchMinAgeRecusrive = (itm: ICartItem) => {
            if (itm.minAge !== undefined && itm.minAge > minAge) minAge = itm.minAge;

            if (itm.options !== undefined && itm.options.length > 0) {
                for (const optionGroup of itm.options) {
                    for (const optionItem of optionGroup.items) {
                        searchMinAgeRecusrive(optionItem);
                    }
                }
            }
        };

        for (const item of offer.cart.items) {
            searchMinAgeRecusrive(item);
        }

        if (minAge === 0) {
            CartController_New.onContinue.dispatch();
            return;
        }

        new MinimumAgePopover(minAge, () => {
            CartController_New.onContinue.dispatch();
        }).show();
    }

    async function onDecreaseItemSelected(cartItem: ICartItem) {
        isPerformingAction.value = true;
        try {
            await TastierClient.context.changeCartItemQuantity(cartItem.id, cartItem.quantity - 1);
        } catch (ex) {
            logError(ex);
        } finally {
            isPerformingAction.value = false;
        }
    }

    async function onIncreaseItemSelected(cartItem: ICartItem) {
        isPerformingAction.value = true;
        try {
            await TastierClient.context.changeCartItemQuantity(cartItem.id, cartItem.quantity + 1);
        } catch (ex) {
            logError(ex);
        } finally {
            isPerformingAction.value = false;
        }
    }

    async function onRemoveItemSelected(cartItem: ICartItem) {
        isPerformingAction.value = true;
        try {
            await TastierClient.context.removeItemFormCart(cartItem.id);
        } catch (ex) {
            logError(ex);
        } finally {
            isPerformingAction.value = false;
        }
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .app > .cart {
        > .itemlist {
            position: absolute;
            top: 53px;
            bottom: 104px;
            left: 0;
            right: 0;
            padding: 0 22px;

            > .item {
                position: relative;
                border-bottom: 1px solid #f5f5f5;
                padding: 24px 0;

                &.has-discount {
                    padding-top: 1.725em;

                    .head {
                        margin-bottom: -0.625em;
                    }
                }

                .item-price.has-discount {
                    cursor: pointer;
                    transition: opacity 0.2s variables.$easeOutExpo;

                    &:hover {
                        opacity: 0.5;
                    }
                }

                > .head {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: nowrap;
                    justify-content: space-between;
                    line-height: 18px;

                    > .quantity {
                        color: var(--colorPrimary);
                        font-size: 16px;
                        font-weight: 600;
                        flex: 0 0 28px;
                    }

                    > .name {
                        font-size: 16px;
                        text-align: left;
                        white-space: normal;
                        flex-grow: 1;
                    }

                    > .increaser {
                        flex: 0 0 52px;
                        font-size: 0;
                        white-space: nowrap;

                        > .btn {
                            display: inline-block;
                            vertical-align: top;
                            background-color: transparent;
                            border: none;
                            padding: 4px;
                            margin: -4px 0;

                            > .outer {
                                height: 18px;
                                width: 18px;
                                border-radius: 50%;
                                text-align: center;

                                > .fas {
                                    line-height: 18px;
                                    font-size: 18px;
                                }
                            }

                            &.decrease {
                                > .outer > .fas {
                                    color: colors.$orange;
                                }

                                &:disabled > .outer > .fas {
                                    color: colors.$orangeHover;
                                }
                            }

                            &.increase {
                                > .outer > .fas {
                                    color: var(--colorPrimary);
                                }

                                &:disabled > .outer > .fas {
                                    color: var(--colorPrimaryHover);
                                }
                            }

                            &:disabled {
                                cursor: pointer !important;
                            }

                            &.disabled {
                                cursor: pointer !important;

                                > .outer > .fas {
                                    color: #cccccc !important;
                                }
                            }
                        }
                    }

                    > .price {
                        flex: 0 0 60px;
                        text-align: right;

                        &.has-discount {
                            position: relative;
                            top: -0.625em;
                        }
                    }

                    > .remover {
                        flex: 0 0 20px;
                        margin: -10px;
                        padding: 10px;
                        text-align: right;
                        cursor: pointer;
                        font-size: 0;

                        > .outer {
                            display: inline-block;
                            vertical-align: top;
                            height: 12px;

                            > .fal {
                                line-height: 16px;
                                font-size: 12px;
                                color: colors.$lightFontColor;
                            }
                        }
                    }
                }

                .subs {
                    padding: 2px 0 2px 22px;

                    > .group {
                        padding-bottom: 4px;

                        > .name {
                            font-style: italic;
                            font-size: 12px;
                            line-height: 18px;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }

                        > .entries {
                            > .item {
                                font-size: 12px;
                                line-height: 20px;

                                > .head {
                                    display: flex;
                                    flex-direction: row;
                                    flex-wrap: nowrap;
                                    justify-content: space-between;

                                    > .name {
                                        > .quantity {
                                            display: inline-block;
                                            vertical-align: top;
                                            padding-right: 3px;
                                            font-weight: 600;
                                        }

                                        flex-grow: 1;
                                    }

                                    > .price {
                                        flex: 0 0 56px;
                                        text-align: right;
                                        padding-right: 20px;
                                    }
                                }
                            }
                        }
                    }
                }

                > .subs {
                    padding-top: 6px;
                    padding-left: 28px;
                    margin-bottom: -12px;
                }

                > .comment-editor-container,
                > .cart-item-comment-editor {
                    padding-left: 28px;
                    margin-bottom: -12px;
                }
            }

            > .nodata {
                padding: 40px 20px 0 20px;

                > .lbl {
                    text-align: center;
                    font-size: 14px;
                    padding: 12px 0;
                }

                > .backlink {
                    color: var(--colorPrimary);
                    text-decoration: none;
                    display: block;
                    text-align: center;
                    padding: 4px;
                    font-size: 14px;
                    cursor: pointer;

                    @include mixins.hover-and-active() {
                        color: var(--colorPrimaryHover);
                    }
                }
            }
        }

        > .bottom {
            height: 104px;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 220;
            padding: 0 12px;
            background-color: #fff;
            -webkit-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
            -moz-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
            box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);

            > .selectionsum {
                text-align: center;
                font-size: 14px;
                color: colors.$fontColor;
                height: 38px;
                line-height: 38px;
                letter-spacing: 0.3px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }
</style>
